import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LabelSheet from "@/components/LabelSheet";
import LabelPrintDialog from "@/components/LabelPrintDialog";
import { Printer, ScanLine, PencilLine, Ban, Undo2 } from "lucide-react";
import { EditDialog, RetireDialog } from "@/components/RecordEditor";
import useLivePoll from "@/hooks/useLivePoll";
import LiveDot from "@/components/LiveDot";

const SLIDE_TYPES = ["H&E", "Unstained", "Recut", "IHC", "Special Stain", "Frozen", "FNA"];

export default function Inventory() {
  const { can, user } = useAuth();
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ q: "", kind: "", checked_out: "", slide_type: "", voided: "" });
  const [labels, setLabels] = useState(null);
  const [printItems, setPrintItems] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [voidItem, setVoidItem] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = { ...filters };
      if (params.voided !== "true") delete params.voided;
      const { data } = await api.get("/specimens", { params });
      setItems(data);
    } catch (e) { toast.error(apiError(e)); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  const lastSync = useLivePoll(load, 5000);

  const toggle = async (s) => {
    try {
      await api.post(`/specimens/${s.id}/${s.checked_out ? "return" : "checkout"}`, { to_whom: user?.name || "", note: "" });
      toast.success(s.checked_out ? `${s.barcode} returned` : `${s.barcode} checked out`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="inventory-page">
      {labels && <LabelSheet items={labels} onClose={() => setLabels(null)} />}
      <LabelPrintDialog open={!!printItems} onOpenChange={(o) => !o && setPrintItems(null)}
                        items={printItems || []}
                        defaultSpec={(printItems || [])[0]?.kind === "block" ? "cassette" : "slide"} />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Blocks & Slides</h1>
          <p className="mt-1 flex items-center gap-2 text-sm text-slate-600">{items.length} item(s) · scan or type a barcode to locate. <LiveDot lastSync={lastSync} testid="inventory-live-indicator" /></p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setPrintItems(items.slice(0, 200))} disabled={!items.length}
                  data-testid="print-labels-printer-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
            <Printer size={15} className="mr-2" /> Label printer
          </Button>
          <Button variant="outline" onClick={() => setLabels(items.slice(0, 60))} disabled={!items.length} data-testid="print-labels-bulk-button" className="h-11">
            <Printer size={15} className="mr-2" /> Quick sheet
          </Button>
        </div>
      </div>

      <div className="grid gap-2 border border-slate-200 bg-slate-50 p-3 md:grid-cols-6" data-testid="inventory-filters">
        <div className="relative md:col-span-2">
          <ScanLine size={16} className="absolute left-3 top-3.5 text-slate-400" />
          <Input
            autoFocus value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="Scan barcode or search accession / patient…" data-testid="barcode-scan-input"
            className="h-11 border-slate-300 bg-white pl-9 font-mono"
          />
        </div>
        <select value={filters.kind} onChange={(e) => setFilters((f) => ({ ...f, kind: e.target.value }))} data-testid="filter-kind-select" className="h-11 rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">Blocks & slides</option><option value="block">Blocks only</option><option value="slide">Slides only</option>
        </select>
        <select value={filters.slide_type} onChange={(e) => setFilters((f) => ({ ...f, slide_type: e.target.value }))} data-testid="filter-slide-type-select" className="h-11 rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">Any slide type</option>
          {SLIDE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filters.checked_out} onChange={(e) => setFilters((f) => ({ ...f, checked_out: e.target.value }))} data-testid="filter-checkout-select" className="h-11 rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">Any status</option><option value="true">Checked out</option><option value="false">In inventory</option>
        </select>
        <select value={filters.voided} onChange={(e) => setFilters((f) => ({ ...f, voided: e.target.value }))} data-testid="filter-voided-select" className="h-11 rounded-md border border-slate-300 bg-white px-3 text-sm">
          <option value="">Active only</option><option value="true">Voided only</option>
        </select>
      </div>

      <div className="overflow-x-auto border border-slate-200">
        <table className="w-full min-w-[900px] text-sm" data-testid="inventory-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
            <tr className="border-b border-slate-200">
              <th className="px-3 py-2">Barcode</th><th className="px-3 py-2">Case</th><th className="px-3 py-2">Patient</th>
              <th className="px-3 py-2">Type</th><th className="px-3 py-2">Block / Level</th><th className="px-3 py-2">Stain</th>
              <th className="px-3 py-2">Status</th><th className="px-3 py-2">Logged</th><th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-b border-slate-100 row-hover hover:bg-slate-50" data-testid={`inventory-row-${s.barcode}`}>
                <td className="px-3 py-2 font-mono font-semibold text-slate-800">{s.barcode}</td>
                <td className="px-3 py-2">
                  <Link to={`/cases/${s.case_id}`} className="font-mono text-[#134e4a] hover:underline">{s.accession_number}</Link>
                </td>
                <td className="px-3 py-2">{s.patient_name}</td>
                <td className="px-3 py-2">{s.kind === "slide" ? (s.slide_type || s.stain || "Slide") : "Block"}</td>
                <td className="px-3 py-2 num text-slate-700">
                  {s.kind === "slide"
                    ? `Block ${s.block_designation || "—"} · L${s.level || "—"}`
                    : `Block ${s.block_designation || s.label}`}
                </td>
                <td className="px-3 py-2">{s.stain || "—"}</td>
                <td className="px-3 py-2">
                  {s.checked_out
                    ? <span className="bg-amber-700 px-1.5 py-0.5 text-[11px] font-semibold text-white">Out · {s.checked_out_to}</span>
                    : <span className="text-green-800">In inventory</span>}
                </td>
                <td className="px-3 py-2 num text-slate-600">{fmtDateTime(s.created_at)}</td>
                <td className="px-3 py-2 text-right">
                  {can("inventory:write") && (
                    <div className="flex flex-wrap justify-end gap-1">
                      {s.voided ? (
                        <Button size="sm" variant="outline" data-testid={`inventory-restore-${s.barcode}`} className="min-h-[36px]"
                                onClick={async () => {
                                  try {
                                    await api.post(`/specimens/${s.id}/restore`);
                                    toast.success(`${s.barcode} reinstated`);
                                    load();
                                  } catch (e) { toast.error(apiError(e)); }
                                }}>
                          <Undo2 size={13} className="mr-1" /> Reinstate
                        </Button>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" onClick={() => toggle(s)} data-testid={`inventory-toggle-${s.barcode}`} className="min-h-[36px]">
                            {s.checked_out ? "Return" : "Check out"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditItem(s)}
                                  data-testid={`inventory-correct-${s.barcode}`} className="min-h-[36px]">
                            <PencilLine size={13} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setVoidItem(s)}
                                  data-testid={`inventory-void-${s.barcode}`} className="min-h-[36px] border-red-300 text-red-800 hover:bg-red-50">
                            <Ban size={13} />
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-sm text-slate-500" data-testid="inventory-empty">
                No blocks or slides found.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <EditDialog
        open={!!editItem} onOpenChange={(v) => !v && setEditItem(null)} testid="correct-specimen-dialog"
        title={`Correct ${editItem?.barcode || ""}`}
        description="Fix a mistyped barcode, label or level. The case, history and checkout trail stay intact."
        values={editItem || {}}
        fields={[
          { name: "barcode", label: "Barcode", hint: "Must stay unique across the lab" },
          { name: "label", label: "Label" },
          { name: "part", label: "Part" },
          { name: "level", label: "Level", type: "number" },
          { name: "slide_type", label: "Slide type", type: "select", allowEmpty: true, options: SLIDE_TYPES },
          { name: "notes", label: "Notes", type: "textarea", span: 2 },
        ]}
        onSave={async (body) => {
          await api.put(`/specimens/${editItem.id}`, body);
          toast.success("Block/slide corrected");
          load();
        }}
      />

      <RetireDialog
        open={!!voidItem} onOpenChange={(v) => !v && setVoidItem(null)} testid="void-specimen-dialog"
        title={`Void ${voidItem?.barcode || ""}`} confirmLabel="Void block/slide"
        description="Use this when a block or slide was logged in error. It leaves the inventory but stays on file under the Voided filter."
        onConfirm={async (reason) => {
          await api.post(`/specimens/${voidItem.id}/void`, { reason });
          toast.success("Voided");
          load();
        }}
      />
    </div>
  );
}
